/**
 * ForgotPasswordPage — request a password reset link.
 *
 * The backend returns the same response shape for known and unknown
 * emails to avoid enumeration, so the UI also lands on the same
 * "check your email" confirmation either way. A user who mistyped
 * their address sees the success page, then notices no email arrives.
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setIsLoading(true);
    setError(null);
    try {
      // Fire-and-display: regardless of the body, the backend returns
      // 200 with a uniform message. We only need to know the request
      // completed without a network error before swapping the view.
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      if (!res.ok && res.status >= 500) {
        throw new Error("Server error");
      }
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-brand-navy">
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-lg bg-brand-crimson flex items-center justify-center">
              <svg
                className="w-6 h-6 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5"></path>
                <path d="M2 12l10 5 10-5"></path>
              </svg>
            </div>
            <span className="text-3xl font-bold text-white tracking-tight">XYDEN</span>
          </div>
          <p className="text-slate-400 text-sm">Leadership Maturity Assessment Platform</p>
        </div>

        <div className="bg-white rounded-lg shadow-2xl p-8">
          {submitted ? (
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Check your email</h1>
              <p className="text-sm text-slate-600 mt-2">
                If an account exists for <span className="font-medium">{email.trim()}</span>, a password reset
                link has been sent. The link is valid for one hour.
              </p>
              <p className="text-sm text-slate-500 mt-3">
                Didn't receive it? Check your spam folder, or try again in a few minutes.
              </p>
              <div className="mt-6">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-crimson hover:underline"
                >
                  <ArrowLeft size={14} aria-hidden="true" /> Back to sign in
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="text-xl font-semibold text-slate-900">Forgot your password?</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Enter the email associated with your account and we'll send you a reset link.
                </p>
              </div>

              {error && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm font-medium"
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate aria-label="Forgot password" className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError(null);
                    }}
                    className="w-full border border-brand-steel/50 bg-white rounded-lg px-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-crimson/30"
                    placeholder="you@example.com"
                    aria-label="Email"
                  />
                </div>

                <button
                  type="submit"
                  disabled={!email.trim() || isLoading}
                  className="w-full py-2.5 bg-brand-crimson text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-crimson/90"
                >
                  {isLoading ? "Sending..." : "Send reset link"}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-crimson hover:underline"
                >
                  <ArrowLeft size={14} aria-hidden="true" /> Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}

export default ForgotPasswordPage;
